import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../lib/supabase';

const MIN_LENGTH = 8;
const hasUpper = (p: string) => /[A-Z]/.test(p);
const hasLower = (p: string) => /[a-z]/.test(p);
const hasNumber = (p: string) => /[0-9]/.test(p);
const hasSpecial = (p: string) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p);

export default function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [recoveryReady, setRecoveryReady] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!supabase) {
      setChecking(false);
      setError('Password reset is not configured.');
      return;
    }

    const hash = window.location.hash;
    if (!hash) {
      setError('Invalid or expired reset link. Please request a new password reset.');
      setChecking(false);
      return;
    }

    const params = new URLSearchParams(hash.substring(1));
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const type = params.get('type');

    if (type === 'recovery' && accessToken && refreshToken) {
      supabase.auth
        .setSession({ access_token: accessToken, refresh_token: refreshToken })
        .then(() => {
          setRecoveryReady(true);
          window.history.replaceState(null, '', window.location.pathname);
        })
        .catch((err) => {
          console.error('Reset link session error:', err);
          setError('Invalid or expired reset link. Please request a new password reset.');
        })
        .finally(() => setChecking(false));
    } else {
      supabase.auth
        .initialize()
        .then((res) => {
          const session = (res as { data?: { session?: unknown } }).data?.session;
          if (session) {
            setRecoveryReady(true);
          } else {
            setError('Invalid or expired reset link. Please request a new password reset.');
          }
        })
        .catch(() => setError('Invalid or expired reset link.'))
        .finally(() => setChecking(false));
    }
  }, []);

  const valid =
    password.length >= MIN_LENGTH &&
    hasUpper(password) &&
    hasLower(password) &&
    hasNumber(password) &&
    hasSpecial(password) &&
    password === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || !supabase) return;
    setError(null);
    setLoading(true);
    try {
      const { error: err } = await supabase.auth.updateUser({ password });
      if (err) throw err;
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!recoveryReady && error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gray-50">
        <div className="max-w-md w-full text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Link to="/forgot-password" className="text-gray-900 font-semibold hover:underline">
            Request a new reset link
          </Link>
          <span className="mx-2">·</span>
          <Link to="/login" className="text-gray-900 font-semibold hover:underline">
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-white">
        <div className="max-w-md w-full text-center">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Password updated</h1>
          <p className="text-gray-500">Redirecting you to sign in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left: Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-8 py-12 bg-white">
        <div className="w-full max-w-md">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Set new password</h1>
          <p className="text-gray-500 mb-8">
            You’re in LIAISON. Enter your new password and confirm it below.
          </p>

          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-900 mb-1.5">
                Enter new password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 pr-11 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent transition"
                  placeholder="Min. 8 characters (industry standard)"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                </button>
              </div>
              <p className="mt-1.5 text-xs text-gray-500">
                At least 8 characters, one upper, one lower, one number, one special character.
              </p>
            </div>

            <div>
              <label htmlFor="confirm" className="block text-sm font-medium text-gray-900 mb-1.5">
                Confirm password
              </label>
              <div className="relative">
                <input
                  id="confirm"
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 pr-11 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent transition"
                  placeholder="Re-enter your new password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  aria-label={showConfirm ? 'Hide password' : 'Show password'}
                >
                  {showConfirm ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                </button>
              </div>
              {confirmPassword.length > 0 && password !== confirmPassword && (
                <p className="mt-1.5 text-sm text-amber-600">Passwords do not match</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !valid}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-900 text-white font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update password'
              )}
            </button>
          </form>

          <p className="text-center text-gray-500 text-sm mt-6">
            <Link to="/login" className="text-gray-900 font-semibold hover:underline">
              Back to sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right: Branding */}
      <div className="hidden lg:flex w-1/2 bg-gray-50 items-center justify-center px-12">
        <div className="max-w-md">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Inventory Reconciliation Tool</h2>
          <p className="text-gray-500 leading-relaxed">
            Set a new password to sign back in. Use at least 8 characters with uppercase, lowercase, a number, and a special character.
          </p>
        </div>
      </div>
    </div>
  );
}
